import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import {
  createBlockedUser,
  deleteBlockedUser,
  getAllBlockedUsers,
} from '@services/api/blocked-user.api'
import {
  fetchCallFilteringSettingsApi,
  saveCallFilteringSettingsApi,
  type ICallFilteringSettings,
} from '@services/api/call-filtering-settings.api'
import type {
  BlockedFeatureKey,
  IBlockedUser,
  IReqBlockedUser,
} from '@dataTypes/blocked-user.type'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useAppSelector } from '@store/hooks'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { BLOCK_REASON_PRESET_VALUES } from '@constants/Settings'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useClinicChange from '../shared/useClinicChange'
import { FilterSelection } from '@enamelai/design-system'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'

/** Matches `id` on the Call Blocking settings root for GlobalLoading overlay */
const LOADING_CONTAINER = 'settings-call-blocking'

export type CallFilteringFlagKey =
  | 'block_unknown_callers'
  | 'block_international_calls'

type CallFilteringFilters = Pick<
  ICallFilteringSettings,
  'block_unknown_callers' | 'block_international_calls'
>

export interface ICallBlockingHooks {
  setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
  setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
  clinicId: string
  loading: boolean
  dataReady: boolean
  filters: CallFilteringFilters
  mainSearchQuery: string
  setMainSearchQuery: Dispatch<SetStateAction<string>>
  savedFilters: CallFilteringFilters
  blockedRows: IBlockedUser[]
  gridRows: Array<
    IBlockedUser & {
      number_display: string
      reason: string
      date_display: string
    }
  >
  mainFilters: FilterSelection
  setMainFilters: Dispatch<SetStateAction<FilterSelection>>
  reasonFilterChoices: string[]
  handleFilterChange: (key: CallFilteringFlagKey, value: boolean) => void
  handleSave: () => Promise<void>
  handleReset: () => void
  refreshBlockedList: () => Promise<void>
  showAddModal: boolean
  setShowAddModal: Dispatch<SetStateAction<boolean>>
  newPhone: string
  setNewPhone: Dispatch<SetStateAction<string>>
  newReason: string
  setNewReason: Dispatch<SetStateAction<string>>
  newFeatures: BlockedFeatureKey[]
  setNewFeatures: Dispatch<SetStateAction<BlockedFeatureKey[]>>
  toggleNewFeature: (key: BlockedFeatureKey) => void
  addBlockedEntry: () => Promise<void>
  removeBlocked: (id: string) => Promise<void>
  hasChanges: boolean
}

const DEFAULT_FILTERS = {
  block_unknown_callers: false,
  block_international_calls: false,
}

function readUserIdFromStorage(): string {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return ''
    const u = JSON.parse(raw) as { user_id?: string }
    return u.user_id ?? ''
  } catch {
    return ''
  }
}

const useCallBlocking = (): ICallBlockingHooks => {
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [mainSearchQuery, setMainSearchQuery] = useState('')
  const [mainFilters, setMainFilters] = useState<FilterSelection>(() => ({
    reason: [],
  }))

  const selectedClinicId = useAppSelector(
    (s) => s.clinic.selectedClinic?.clinic_id ?? '',
  )
  const clinicId =
    selectedClinicId ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('clinic_id') || ''
      : '')
  const userId = useMemo(() => readUserIdFromStorage(), [])

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [savedFilters, setSavedFilters] = useState(DEFAULT_FILTERS)
  const [blockedRows, setBlockedRows] = useState<IBlockedUser[]>([])
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const loadGen = useRef(0)

  useClinicChange(clinicId, () => {
    loadGen.current += 1
    setDataReady(false)
    setLoading(true)
    setBlockedRows([])
  })
  const [showAddModal, setShowAddModal] = useState(false)
  const [newPhone, setNewPhone] = useState('')
  const [newReason, setNewReason] = useState('')
  const [newFeatures, setNewFeatures] = useState<BlockedFeatureKey[]>([
    'incoming_calls',
    'outgoing_calls',
    'sms_chat',
    'fax',
  ])
  const hasChanges = useMemo(
    () =>
      filters.block_unknown_callers !== savedFilters.block_unknown_callers ||
      filters.block_international_calls !==
        savedFilters.block_international_calls,
    [filters, savedFilters],
  )

  useSettingsSubpageLoading(!dataReady)

  const refreshBlockedList = useCallback(async () => {
    if (!clinicId) return
    try {
      const res = await getAllBlockedUsers()
      const list = res.data?.blocked_users ?? []
      setBlockedRows(Array.isArray(list) ? list : [])
    } catch (e) {
      setBlockedRows([])
    }
  }, [clinicId])

  const loadSettings = useCallback(async () => {
    const gen = ++loadGen.current
    if (!clinicId) {
      setLoading(false)
      setDataReady(true)
      return
    }
    setLoading(true)
    try {
      const res = await fetchCallFilteringSettingsApi(clinicId)
      const s = res.data?.call_filtering_settings
      if (s) {
        const next = {
          block_unknown_callers: Boolean(s.block_unknown_callers),
          block_international_calls: Boolean(s.block_international_calls),
        }
        setFilters(next)
        setSavedFilters(next)
      }
      await refreshBlockedList()
    } catch (e) {
      showToast(
        createFailedToast(
          'Call blocking',
          'Could not load call blocking settings.',
        ),
      )
    } finally {
      if (loadGen.current === gen) {
        setLoading(false)
        setDataReady(true)
      }
    }
  }, [clinicId, refreshBlockedList, showToast])

  useEffect(() => {
    void loadSettings()
  }, [loadSettings])

  useEffect(() => {
    if (!hasChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasChanges])

  const handleFilterChange = useCallback(
    (key: CallFilteringFlagKey, value: boolean): void => {
      setFilters((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const reasonFilterChoices = useMemo(() => {
    const values = new Set<string>(BLOCK_REASON_PRESET_VALUES)
    for (const row of blockedRows) {
      const features = Array.isArray(row.blocked_features)
        ? row.blocked_features
        : []
      const isCallRelated =
        features.includes('incoming_calls') ||
        features.includes('outgoing_calls')
      if (!isCallRelated) continue
      const r = String(row.reason ?? '').trim()
      if (r) values.add(r)
    }
    const sorted = Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    )
    return sorted
  }, [blockedRows])

  const gridRows = useMemo(() => {
    const q = mainSearchQuery.trim().toLowerCase()
    const selectedReasons = mainFilters.reason ?? []
    const showAllReasons = selectedReasons.length === 0

    return blockedRows
      .filter((row) => {
        const features = Array.isArray(row.blocked_features)
          ? row.blocked_features
          : []
        const isCallRelated =
          features.includes('incoming_calls') ||
          features.includes('outgoing_calls')
        if (!isCallRelated) return false

        if (!showAllReasons) {
          const reasonNorm = String(row.reason ?? '').trim()
          if (!selectedReasons.includes(reasonNorm)) return false
        }

        const numberDisplay =
          formatPhoneNumber(row.phone_number ?? '') || row.phone_number || ''
        const reasonStr = row.reason ?? ''
        const dateDisplay = row.date_time_created_at
          ? new Date(row.date_time_created_at).toLocaleDateString()
          : ''

        if (!q) return true

        const raw = String(row.phone_number ?? '')
        const rawLower = raw.toLowerCase()
        const digits = raw.replace(/\D/g, '')
        const dispLower = numberDisplay.toLowerCase()
        const dateLower = dateDisplay.toLowerCase()
        let isoLower = ''
        if (row.date_time_created_at) {
          const d = new Date(row.date_time_created_at)
          if (!Number.isNaN(d.getTime())) {
            isoLower = d.toISOString().slice(0, 10).toLowerCase()
          }
        }
        const reasonLower = reasonStr.toLowerCase()
        const hay = `${dispLower} ${rawLower} ${digits} ${dateLower} ${isoLower} ${reasonLower}`
        return hay.includes(q)
      })
      .map((row) => ({
        ...row,
        number_display:
          formatPhoneNumber(row.phone_number ?? '') || row.phone_number || '',
        reason: row.reason ?? '',
        date_display: row.date_time_created_at
          ? new Date(row.date_time_created_at).toLocaleDateString()
          : '',
      }))
  }, [blockedRows, mainSearchQuery, mainFilters])

  const handleSave = useCallback(async () => {
    if (!clinicId) {
      showToast(createFailedToast('Call blocking', 'No clinic selected.'))
      return
    }
    showLoading({ container: LOADING_CONTAINER })
    try {
      await saveCallFilteringSettingsApi({
        clinic_id: clinicId,
        ...filters,
        updated_by: userId || undefined,
      })
      setSavedFilters(filters)
      showToast(createSuccessToast('Saved', 'Call blocking settings saved.'))
    } catch (e) {
      showToast(createFailedToast('Call blocking', 'Save failed.'))
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [clinicId, filters, hideLoading, showLoading, showToast, userId])

  const handleReset = useCallback(() => {
    setFilters({ ...savedFilters })
  }, [savedFilters])

  const toggleNewFeature = useCallback((key: BlockedFeatureKey) => {
    setNewFeatures((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }, [])

  const addBlockedEntry = useCallback(async () => {
    if (!clinicId || !userId) {
      showToast(createFailedToast('Call blocking', 'Missing clinic or user.'))
      return
    }
    if (!newPhone.trim()) {
      showToast(createFailedToast('Call blocking', 'Enter a phone number.'))
      return
    }
    if (!newReason.trim()) {
      showToast(createFailedToast('Call blocking', 'Enter a reason.'))
      return
    }
    if (newFeatures.length === 0) {
      showToast(
        createFailedToast(
          'Call blocking',
          'Select at least one blocked feature.',
        ),
      )
      return
    }
    showLoading({ container: LOADING_CONTAINER })
    try {
      const body: IReqBlockedUser = {
        clinic_id: clinicId,
        contact_id: null,
        id: null,
        phone_number: newPhone.trim(),
        reason: newReason.trim(),
        blocked_features: newFeatures,
        created_by: userId,
        is_hidden: false,
      }
      await createBlockedUser(body)
      showToast(createSuccessToast('Blocked', 'Number blocked.'))
      setShowAddModal(false)
      setNewPhone('')
      setNewReason('')
      setNewFeatures(['incoming_calls', 'outgoing_calls', 'sms_chat', 'fax'])
      await refreshBlockedList()
    } catch (e) {
      showToast(
        createFailedToast('Call blocking', 'Could not add blocked number.'),
      )
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    clinicId,
    hideLoading,
    newFeatures,
    newPhone,
    newReason,
    refreshBlockedList,
    showLoading,
    showToast,
    userId,
  ])

  const removeBlocked = useCallback(
    async (blockedUserId: string) => {
      showLoading({ container: LOADING_CONTAINER })
      try {
        await deleteBlockedUser(blockedUserId)
        showToast(createSuccessToast('Removed', 'Removed from blocked list.'))
        await refreshBlockedList()
      } catch (e) {
        showToast(createFailedToast('Call blocking', 'Could not remove entry.'))
      } finally {
        hideLoading(LOADING_CONTAINER)
      }
    },
    [hideLoading, refreshBlockedList, showLoading, showToast],
  )

  return {
    setHeaderButtons,
    setHeaderTitle,
    clinicId,
    loading,
    dataReady,
    filters,
    savedFilters,
    blockedRows,
    gridRows,
    mainSearchQuery,
    setMainSearchQuery,
    mainFilters,
    setMainFilters,
    reasonFilterChoices,
    handleFilterChange,
    handleSave,
    handleReset,
    refreshBlockedList,
    showAddModal,
    setShowAddModal,
    newPhone,
    setNewPhone,
    newReason,
    setNewReason,
    newFeatures,
    setNewFeatures,
    toggleNewFeature,
    addBlockedEntry,
    removeBlocked,
    hasChanges,
  }
}

export default useCallBlocking
